import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { TurnoverDetail, YukiTransactionItem } from '../../models/Financial/DivisionTurnoverOverview';
import { FinancialService } from '../financial.service';

type SortColumn = 'documentDate' | 'invoiceNumber' | 'projectName' | 'invoiceSubject' | 'contactName' | 'amount';
type SortDirection = 'asc' | 'desc';

@Component({
    selector: 'app-division-detail-modal',
    templateUrl: './division-detail-modal.component.html',
    styleUrls: ['./division-detail-modal.component.scss'],
})
export class DivisionDetailModalComponent implements OnInit {
    @Input() divisionLabel = '';
    @Input() monthLabel = '';
    @Input() year: number;
    @Input() amount = 0;
    @Input() target = 0;
    @Input() details: TurnoverDetail[] = [];
    @Input() yukiTransactions: YukiTransactionItem[] = [];
    @Input() yukiTotal = 0;
    @Input() prevMonthLabel = '';
    @Input() prevMonthResumoInvoices: TurnoverDetail[] = [];
    @Input() prevMonthYukiTransactions: YukiTransactionItem[] = [];
    @Input() nextMonthLabel = '';
    @Input() nextMonthResumoInvoices: TurnoverDetail[] = [];
    @Input() nextMonthYukiTransactions: YukiTransactionItem[] = [];
    @Input() otherDivisionsYuki: { division: string, transactions: YukiTransactionItem[] }[] = [];

    sortColumn: SortColumn = 'amount';
    sortDirection: SortDirection = 'desc';
    sortedDetails: TurnoverDetail[] = [];
    offsetDetails = new Set<TurnoverDetail>();

    // AI analysis
    analyzing = false;
    analysisHtml: SafeHtml = '';
    hasAnalysis = false;

    constructor(
        public activeModal: NgbActiveModal,
        private financialService: FinancialService,
        private sanitizer: DomSanitizer,
    ) {}

    ngOnInit(): void {
        this.markOffsetInvoices();
        this.applySorting();
    }

    get achievement(): number {
        if (this.target <= 0) return 0;
        return Math.round((this.amount / this.target) * 100);
    }

    get difference(): number {
        return this.amount - this.target;
    }

    sort(column: SortColumn): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = column === 'amount' ? 'desc' : 'asc';
        }
        this.applySorting();
    }

    isOffset(detail: TurnoverDetail): boolean {
        return this.offsetDetails.has(detail);
    }

    private markOffsetInvoices(): void {
        this.offsetDetails.clear();

        const invoicesByProjectAndAmount = new Map<string, TurnoverDetail[]>();
        this.details
            .filter(detail => (detail.amount || 0) > 0)
            .forEach(detail => {
                const key = this.getOffsetMatchKey(detail.projectName, detail.amount);
                if (!key) return;

                const invoices = invoicesByProjectAndAmount.get(key) || [];
                invoices.push(detail);
                invoicesByProjectAndAmount.set(key, invoices);
            });

        this.details
            .filter(detail => (detail.amount || 0) < 0)
            .forEach(creditNote => {
                const key = this.getOffsetMatchKey(creditNote.projectName, Math.abs(creditNote.amount));
                const matchingInvoices = key ? invoicesByProjectAndAmount.get(key) : null;
                if (!matchingInvoices || matchingInvoices.length === 0) return;

                const invoice = this.takeMatchingInvoice(matchingInvoices, creditNote);
                if (!invoice) return;

                this.offsetDetails.add(invoice);
                this.offsetDetails.add(creditNote);
            });
    }

    private getOffsetMatchKey(projectName: string, amount: number): string | null {
        const normalizedProject = (projectName || '').trim().toLowerCase();
        const normalizedAmountInCents = Math.round(Math.abs(amount || 0) * 100);

        if (!normalizedProject || normalizedAmountInCents === 0) return null;

        return `${normalizedProject}::${normalizedAmountInCents}`;
    }

    private takeMatchingInvoice(invoices: TurnoverDetail[], creditNote: TurnoverDetail): TurnoverDetail | null {
        if (invoices.length === 0) return null;

        const creditNumber = this.parseInvoiceNumber(creditNote.invoiceNumber);
        let matchIndex = -1;

        if (creditNumber !== null) {
            let closestPreviousNumber = -Infinity;
            invoices.forEach((invoice, index) => {
                const invoiceNumber = this.parseInvoiceNumber(invoice.invoiceNumber);
                if (invoiceNumber !== null && invoiceNumber < creditNumber && invoiceNumber > closestPreviousNumber) {
                    closestPreviousNumber = invoiceNumber;
                    matchIndex = index;
                }
            });
        }

        const index = matchIndex >= 0 ? matchIndex : 0;
        return invoices.splice(index, 1)[0] || null;
    }

    private parseInvoiceNumber(invoiceNumber: string): number | null {
        const digits = (invoiceNumber || '').replace(/\D/g, '');
        if (!digits) return null;

        const parsed = Number(digits);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private applySorting(): void {
        this.sortedDetails = [...this.details].sort((a, b) => {
            let valA: any = a[this.sortColumn];
            let valB: any = b[this.sortColumn];

            if (this.sortColumn === 'amount') {
                valA = valA || 0;
                valB = valB || 0;
            } else {
                valA = (valA || '').toString().toLowerCase();
                valB = (valB || '').toString().toLowerCase();
            }

            let result: number;
            if (valA < valB) result = -1;
            else if (valA > valB) result = 1;
            else result = 0;

            return this.sortDirection === 'asc' ? result : -result;
        });
    }

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('nl-NL', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    }

    get hasDifference(): boolean {
        return this.yukiTransactions.length > 0 && Math.abs(this.amount - this.yukiTotal) > 0.50;
    }

    analyzeDifference(): void {
        this.analyzing = true;
        this.hasAnalysis = false;
        this.analysisHtml = '';

        const body: any = {
            division: this.divisionLabel,
            period: `${this.monthLabel} ${this.year}`,
            resumoTotal: this.amount,
            yukiTotal: this.yukiTotal,
            resumoInvoices: this.details.map(d => ({
                date: d.documentDate,
                invoiceNumber: d.invoiceNumber,
                subject: d.invoiceSubject,
                contact: d.contactName,
                amount: d.amount,
            })),
            yukiTransactions: this.yukiTransactions.map(tx => ({
                date: tx.date,
                description: tx.description,
                amount: tx.amount,
            })),
        };

        // Include adjacent months for context
        if (this.prevMonthResumoInvoices.length > 0 || this.prevMonthYukiTransactions.length > 0) {
            body.prevMonthLabel = this.prevMonthLabel;
            body.prevMonthResumoInvoices = this.prevMonthResumoInvoices.map(d => ({
                date: d.documentDate, invoiceNumber: d.invoiceNumber, subject: d.invoiceSubject, contact: d.contactName, amount: d.amount,
            }));
            body.prevMonthYukiTransactions = this.prevMonthYukiTransactions.map(tx => ({
                date: tx.date, description: tx.description, amount: tx.amount,
            }));
        }
        if (this.nextMonthResumoInvoices.length > 0 || this.nextMonthYukiTransactions.length > 0) {
            body.nextMonthLabel = this.nextMonthLabel;
            body.nextMonthResumoInvoices = this.nextMonthResumoInvoices.map(d => ({
                date: d.documentDate, invoiceNumber: d.invoiceNumber, subject: d.invoiceSubject, contact: d.contactName, amount: d.amount,
            }));
            body.nextMonthYukiTransactions = this.nextMonthYukiTransactions.map(tx => ({
                date: tx.date, description: tx.description, amount: tx.amount,
            }));
        }

        // Include other GL accounts for cross-checking
        if (this.otherDivisionsYuki.length > 0) {
            body.otherAccountTransactions = this.otherDivisionsYuki.map(d => ({
                division: d.division,
                transactions: d.transactions.map(tx => ({
                    date: tx.date, description: tx.description, amount: tx.amount,
                })),
            }));
        }

        this.financialService.analyzeDifference(body).subscribe({
            next: (result) => {
                this.analysisHtml = this.sanitizer.bypassSecurityTrustHtml(this.formatAnalysis(result.analysis));
                this.hasAnalysis = true;
                this.analyzing = false;
            },
            error: () => {
                this.analysisHtml = this.sanitizer.bypassSecurityTrustHtml('<p class="text--red">Fout bij het analyseren van het verschil.</p>');
                this.hasAnalysis = true;
                this.analyzing = false;
            },
        });
    }

    private formatAnalysis(text: string): string {
        if (!text) return '';
        let html = text
            // Strip any remaining markdown bold/italic
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            // Headers (### / ## / #)
            .replace(/^###\s*(.+)$/gm, '<h6>$1</h6>')
            .replace(/^##\s*(.+)$/gm, '<h5>$1</h5>')
            .replace(/^#\s*(.+)$/gm, '<h4>$1</h4>')
            // Section labels (SAMENVATTING:, DETAILS:, CONCLUSIE:)
            .replace(/^(SAMENVATTING|DETAILS|CONCLUSIE)\s*:?\s*$/gm, '<h5 class="ai-section-title">$1</h5>')
            // Bullet points
            .replace(/^[-•]\s+(.+)$/gm, '<li>$1</li>');

        // Wrap consecutive <li> in <ul>
        html = html.replace(/((?:<li>.*?<\/li>\s*)+)/g, '<ul>$1</ul>');

        // Paragraphs: split by double newline
        const parts = html.split(/\n{2,}/);
        html = parts.map(p => {
            p = p.trim();
            if (!p) return '';
            // Don't wrap block elements in <p>
            if (p.startsWith('<h') || p.startsWith('<ul') || p.startsWith('<li')) return p;
            // Replace single newlines with <br>
            return '<p>' + p.replace(/\n/g, '<br>') + '</p>';
        }).filter(p => p).join('');

        return html;
    }
}
